import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'
import { uid } from '@zera-admin/utils'

import { Data, Dispatch, Props, State } from './types'
import reducer from './reducer'

const initialState: State = []

const initialValue: Props = {
  dispatchs: {
    add: () => null,
    remove: () => null,
  },
  provided: false,
  state: initialState,
}

export const PopupContext = createContext<Props>(initialValue)

export const PopupProvider = ({
  children,
}: {
  children: React.ReactElement
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const add = useCallback(({ id, appearance, ...rest }: Data) => {
    dispatch({
      type: 'ADD_POPUP',
      payload: {
        id: id || uid(),
        appearance: appearance || 'error',
        ...rest,
      },
    })
  }, [])

  const remove = useCallback((id: string) => {
    dispatch({
      type: 'REMOVE_POPUP',
      payload: id,
    })
  }, [])

  const dispatchs = useMemo(
    () => ({
      add,
      remove,
    }),
    [add, remove]
  )

  return (
    <PopupContext.Provider
      value={{
        dispatchs,
        provided: true,
        state,
      }}
    >
      {children}
    </PopupContext.Provider>
  )
}

export const usePopupContext = (): [Dispatch, State] => {
  const { state, provided, dispatchs } = useContext(PopupContext)

  if (!provided) {
    throw new Error('usePopupContext must be used within a PopupProvider.')
  }

  return [dispatchs, state]
}
